import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, Save, Upload, Plus, Pencil, Trash2, Eye, EyeOff,
  GripVertical, ArrowUp, ArrowDown
} from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import api from '@/lib/api';

export function SectionsTab() {
  const { t } = useI18n();
  const [sections, setSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editIdx, setEditIdx] = useState(-1);
  const [editData, setEditData] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get('/cms/sections').then(r => setSections(r.data)).catch(() => {});
  }, []);

  const toggleVisibility = (idx) => {
    const u = [...sections]; u[idx] = { ...u[idx], is_visible: !u[idx].is_visible }; setSections(u);
  };
  const moveSection = (idx, dir) => {
    const u = [...sections]; const t2 = idx + dir;
    if (t2 < 0 || t2 >= u.length) return;
    [u[idx], u[t2]] = [u[t2], u[idx]]; setSections(u);
  };
  const removeSection = (idx) => {
    setSections(sections.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/sections', { sections });
      toast.success(t('cms.save_ok'));
    } catch { toast.error(t('common.error')); }
    finally { setSaving(false); }
  };

  const openEdit = (idx) => {
    setEditIdx(idx);
    setEditData({ ...sections[idx] });
    setEditDialog(true);
  };

  const openAdd = () => {
    setEditIdx(-1);
    setEditData({
      section_id: `section_${Date.now()}`,
      type: 'custom',
      title: '',
      subtitle: '',
      content: '',
      is_visible: true,
      image_url: '',
      image_position: 'right',
      button_label: '',
      button_url: '',
    });
    setEditDialog(true);
  };

  const saveEdit = () => {
    if (!editData.title) { toast.error('Le titre est requis'); return; }
    if (editIdx >= 0) {
      const u = [...sections]; u[editIdx] = { ...u[editIdx], ...editData }; setSections(u);
    } else {
      setSections([...sections, { ...editData, order: sections.length }]);
    }
    setEditDialog(false);
  };

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData(); fd.append('file', file);
        const resp = await api.post('/cms/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setEditData(prev => ({ ...prev, image_url: resp.data.url }));
        toast.success('Image uploadee');
      } catch { toast.error('Erreur upload'); }
      finally { setUploading(false); }
    };
    input.click();
  };

  const sectionTypeLabel = {
    hero: 'Hero', features: 'Fonctionnalites', steps: 'Etapes',
    about: 'A propos', news: 'Actualites', cta: 'Appel a l\'action', custom: 'Section personnalisee',
  };

  const internalUrls = [
    { label: 'Inscription', value: '/register' },
    { label: 'Connexion', value: '/login' },
    { label: 'Verifier une carte', value: '/verify' },
    { label: 'Blog / Actualites', value: '/#news' },
    { label: 'A propos', value: '/#about' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">{t('sections.desc')}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openAdd} data-testid="add-section-btn">
            <Plus className="w-4 h-4 mr-1.5" />{t('sections.add')}
          </Button>
          <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={handleSave} disabled={saving} data-testid="save-sections-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {t('cms.save')}
          </Button>
        </div>
      </div>

      <Card className="border">
        <CardContent className="p-4 space-y-2">
          {sections.length === 0 && <p className="text-center text-muted-foreground py-6 text-sm">{t('sections.empty')}</p>}
          {sections.map((section, idx) => (
            <div key={section.section_id || idx} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-white hover:bg-muted/50 transition-colors" data-testid={`section-${section.section_id}`}>
              <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
              {section.image_url && (
                <img src={section.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${section.image_url}` : section.image_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{section.title || sectionTypeLabel[section.type] || 'Section'}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="text-[10px]">{sectionTypeLabel[section.type] || section.type}</Badge>
                  {section.button_label && <Badge variant="secondary" className="text-[10px]">{section.button_label}</Badge>}
                  {section.image_url && <Badge variant="secondary" className="text-[10px]">Image {section.image_position === 'left' ? 'gauche' : 'droite'}</Badge>}
                </div>
              </div>
              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="sm" onClick={() => moveSection(idx, -1)} disabled={idx === 0}><ArrowUp className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1}><ArrowDown className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => openEdit(idx)} data-testid={`edit-section-${idx}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => toggleVisibility(idx)}>
                  {section.is_visible ? <Eye className="w-4 h-4 text-[#009543]" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                </Button>
                {section.type === 'custom' && (
                  <Button variant="ghost" size="sm" onClick={() => removeSection(idx)} data-testid={`delete-section-${idx}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="section-dialog-desc">
          <DialogHeader>
            <DialogTitle>{editIdx >= 0 ? t('sections.edit') : t('sections.add')}</DialogTitle>
            <p id="section-dialog-desc" className="text-sm text-muted-foreground">{t('sections.edit_desc')}</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>{t('sections.title')}</Label>
              <Input value={editData.title || ''} onChange={e => setEditData({ ...editData, title: e.target.value })} placeholder="Titre de la section" data-testid="section-title-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('sections.subtitle')}</Label>
              <Input value={editData.subtitle || ''} onChange={e => setEditData({ ...editData, subtitle: e.target.value })} placeholder="Sous-titre (optionnel)" data-testid="section-subtitle-input" />
            </div>
            <div className="space-y-2">
              <Label>{t('sections.content')}</Label>
              <Textarea value={editData.content || ''} onChange={e => setEditData({ ...editData, content: e.target.value })} placeholder="Contenu de la section (supporte le HTML)" rows={5} data-testid="section-content-input" />
            </div>

            <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
              <Label className="font-medium">{t('sections.image')}</Label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={handleImageUpload} disabled={uploading} data-testid="section-upload-image-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  {t('sections.upload_image')}
                </Button>
                {editData.image_url && (
                  <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => setEditData({ ...editData, image_url: '' })}>
                    <Trash2 className="w-4 h-4 mr-1" />{t('sections.remove_image')}
                  </Button>
                )}
              </div>
              {editData.image_url && (
                <div className="flex items-start gap-4 mt-2">
                  <img src={editData.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${editData.image_url}` : editData.image_url} alt="Preview" className="w-32 h-20 object-cover rounded border" />
                  <div className="space-y-2 flex-1">
                    <Label>{t('sections.image_position')}</Label>
                    <Select value={editData.image_position || 'right'} onValueChange={v => setEditData({ ...editData, image_position: v })}>
                      <SelectTrigger data-testid="section-image-position"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">{t('sections.pos_left')}</SelectItem>
                        <SelectItem value="right">{t('sections.pos_right')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
              <Label className="font-medium">{t('sections.button')}</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t('sections.button_label')}</Label>
                  <Input value={editData.button_label || ''} onChange={e => setEditData({ ...editData, button_label: e.target.value })} placeholder="En savoir plus" data-testid="section-button-label" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('sections.button_url')}</Label>
                  <Input value={editData.button_url || ''} onChange={e => setEditData({ ...editData, button_url: e.target.value })} placeholder="https://... ou /register" data-testid="section-button-url" />
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <span className="text-xs text-muted-foreground mr-1">{t('sections.url_suggestions')} :</span>
                {internalUrls.map(u => (
                  <Badge key={u.value} variant="outline" className="text-[10px] cursor-pointer hover:bg-[#007FFF]/10 hover:border-[#007FFF]"
                    onClick={() => setEditData({ ...editData, button_url: u.value, button_label: editData.button_label || u.label })}>
                    {u.label} ({u.value})
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditDialog(false)}>{t('sections.cancel')}</Button>
            <Button className="bg-[#007FFF] hover:bg-[#0066CC]" onClick={saveEdit} data-testid="section-save-edit-btn">
              <Save className="w-4 h-4 mr-1.5" />{editIdx >= 0 ? t('sections.update') : t('sections.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
